// Hangar: pick a mech (locked mechs can be bought with cores), reactor colour,
// meta unlocks, challenge toggles, then LAUNCH.

import Phaser from 'phaser';
import { iconKey } from '../art/keys';
import { SCENES } from '../config/constants';
import { COLORS } from '../config/palette';
import type { MechViewApi } from '../core/contracts';
import { startRun } from '../core/run';
import { save } from '../core/save';
import type { MechId } from '../core/types';
import { MECHS, MECH_ORDER } from '../data/mechs';
import { CHALLENGES, COSMETICS, UNLOCKS, buyUnlock, reactorColorFor } from '../data/unlocks';
import { createMechView } from '../systems/player/MechView';
import { gridBackdrop, hangarBay } from '../ui/backdrops';
import { MenuNav, Panel, TYPE, UI, createButton, footerHints, iconImage, music, setMenuCursor, sfx, txt } from '../ui/kit';

export class HangarScene extends Phaser.Scene {
  private nav!: MenuNav;
  private mechId: MechId = 'volt';
  private view: MechViewApi | null = null;
  private layer!: Phaser.GameObjects.Container;
  private t = 0;

  constructor() {
    super(SCENES.HANGAR);
  }

  create(): void {
    setMenuCursor(this, true);
    this.mechId = save.isUnlocked(MECHS[save.data.lastMech]?.unlock) ? save.data.lastMech : 'volt';
    if (this.textures.exists('art_hangar')) hangarBay(this);
    else gridBackdrop(this, COLORS.bastion);
    music('hangar');
    this.events.once(Phaser.Scenes.Events.SHUTDOWN, () => this.disposeView());
    this.build();
    this.cameras.main.fadeIn(300, 5, 7, 11);
  }

  private disposeView(): void {
    this.view?.destroy();
    this.view = null;
  }

  /** (Re)build every widget for the current selection. */
  private build(focusLabel?: string): void {
    this.nav?.destroy();
    this.layer?.destroy();
    this.disposeView();
    this.layer = this.add.container(0, 0);
    const mech = MECHS[this.mechId];
    const unlocked = save.isUnlocked(mech.unlock);
    const accent = reactorColorFor(this.mechId);
    const add = <T extends Phaser.GameObjects.GameObject>(o: T): T => {
      this.layer.add(o);
      return o;
    };

    add(txt(this, 70, 40, 'HANGAR', { font: 'display', size: TYPE.h1, weight: 700, spacing: 8 }));
    add(txt(this, UI.W - 70, 54, `CORES ${save.data.cores}`, { font: 'display', size: TYPE.h3, weight: 700, color: COLORS.ultBar, originX: 1 }));

    this.nav = new MenuNav(this, { layout: 'spatial', onBack: () => this.scene.start(SCENES.MENU) });

    // Mech selector.
    let focusBtn: ReturnType<typeof createButton> | null = null;
    MECH_ORDER.forEach((id, i) => {
      const m = MECHS[id];
      const open = save.isUnlocked(m.unlock);
      const cost = UNLOCKS.find((u) => u.id === m.unlock)?.cost ?? 0;
      const btn = createButton(this, 250, 170 + i * 84, {
        label: m.name,
        width: 360,
        height: 68,
        accent: m.reactorColor,
        variant: id === this.mechId ? 'primary' : 'secondary',
        sublabel: open ? m.playstyle.split(' ')[0].toUpperCase() : `${cost} CORES`,
        icon: open ? undefined : iconKey.lock,
        onClick: () => this.selectMech(id),
      }, this.nav);
      add(btn);
      if (id === this.mechId && !focusLabel) focusBtn = btn;
    });

    // Mech display.
    try {
      const v = createMechView(this, this.mechId);
      v.setWeapon(mech.startingWeapon);
      v.setReactorColor(accent);
      v.setScale(3.2);
      v.setPosition(UI.W / 2 - 120, 830);
      v.setAim(-0.15);
      v.play('idle');
      if (!unlocked) v.setAlpha(0.25);
      this.view = v;
    } catch (err) {
      console.warn('[hangar] mech view unavailable', err);
      const key = this.textures.exists(`art_portrait_${this.mechId}`) ? `art_portrait_${this.mechId}` : '';
      if (key) add(this.add.image(UI.W / 2 - 120, 560, key).setScale(0.6));
    }

    // Info panel.
    const px = UI.W - 700;
    const py = 130;
    add(new Panel(this, px, py, 640, 640, { fill: COLORS.uiBg, fillAlpha: 0.9, accent, ticks: true, chamfer: 18 }));
    add(txt(this, px + 40, py + 30, mech.name, { font: 'display', size: TYPE.title, weight: 700, spacing: 8, color: accent }));
    add(txt(this, px + 40, py + 110, mech.tagline, { size: TYPE.body, color: COLORS.uiText }));
    add(txt(this, px + 40, py + 146, mech.playstyle.toUpperCase(), { font: 'display', size: TYPE.label, color: COLORS.uiDim, spacing: 2 }));
    const abilities: [string, string, string, string][] = [
      [iconKey.passive(mech.passive), 'PASSIVE', mech.passiveName, mech.passiveDesc],
      [iconKey.signature(mech.signature), 'SIGNATURE · RMB', mech.signatureName, mech.signatureDesc],
      [iconKey.ultimate(mech.ultimate), 'ULTIMATE · Q', mech.ultimateName, mech.ultimateDesc],
    ];
    abilities.forEach(([ic, label, name, desc], i) => {
      const y = py + 200 + i * 140;
      add(iconImage(this, px + 70, y + 40, ic, 56, accent));
      add(txt(this, px + 120, y, `${label}  ·  ${name}`, { font: 'display', size: TYPE.label, weight: 700, color: COLORS.uiText, spacing: 2 }));
      add(txt(this, px + 120, y + 32, desc, { size: TYPE.small, color: COLORS.uiDim, wrap: 480 }));
    });

    // Reactor colours: 2-column grid under the info panel; cost sits on the right.
    const cosmetics = COSMETICS[this.mechId] ?? [];
    const cw = 314;
    const ch = 52;
    cosmetics.forEach((c, i) => {
      const owned = save.isUnlocked(c.unlock);
      const selected = (save.data.cosmetics[this.mechId] ?? cosmetics[0]?.id) === c.id;
      add(
        createButton(this, px + cw / 2 + (i % 2) * (cw + 12), py + 680 + Math.floor(i / 2) * (ch + 10), {
          label: c.name,
          sublabel: owned ? undefined : `${c.cost}`,
          sublabelColor: COLORS.ultBar,
          icon: owned ? undefined : iconKey.lock,
          width: cw,
          height: ch,
          fontSize: 16,
          accent: c.color,
          variant: selected ? 'primary' : 'secondary',
          onClick: () => this.pickCosmetic(c.id, c.unlock),
        }, this.nav),
      );
    });

    // Challenges.
    let cy = 470;
    add(txt(this, 70, cy, 'CHALLENGES  +20% CORES EACH', { font: 'display', size: TYPE.label, color: COLORS.uiDim, spacing: 3 }));
    cy += 50;
    for (const ch of CHALLENGES) {
      const open = save.isUnlocked(ch.unlock);
      const on = save.data.challenges.includes(ch.id);
      add(
        createButton(this, 250, cy, {
          label: ch.name,
          width: 360,
          height: 52,
          fontSize: 20,
          variant: on ? 'danger' : 'ghost',
          sublabel: open ? (on ? 'ON' : 'OFF') : 'LOCKED',
          disabled: !open,
          onClick: () => this.toggleChallenge(ch.id),
        }, this.nav),
      );
      cy += 62;
    }

    // Armory unlocks (weapons / equipment / mutations).
    const shop = UNLOCKS.filter((u) => ['weapon', 'equipment', 'mutation'].includes(u.category) && !save.isUnlocked(u.id));
    if (shop.length) {
      add(txt(this, 70, 790, 'ARMORY', { font: 'display', size: TYPE.label, color: COLORS.uiDim, spacing: 3 }));
      shop.slice(0, 3).forEach((u, i) => {
        add(
          createButton(this, 250, 850 + i * 62, {
            label: u.name,
            width: 360,
            height: 52,
            fontSize: 18,
            sublabel: `${u.cost}`,
            sublabelColor: COLORS.ultBar,
            disabled: save.data.cores < u.cost,
            onClick: () => this.buy(u.id),
          }, this.nav),
        );
      });
    }

    const launch = createButton(this, UI.W - 380, 960, {
      label: unlocked ? 'LAUNCH' : 'LOCKED',
      width: 520,
      height: 84,
      variant: 'primary',
      accent,
      hint: 'confirm',
      disabled: !unlocked,
      onClick: () => this.launch(),
    }, this.nav);
    add(launch);
    add(footerHints(this, [
      { action: 'navigateV', label: 'Select' },
      { action: 'confirm', label: 'Confirm' },
      { action: 'back', label: 'Menu' },
    ]));
    this.nav.focus(unlocked ? launch : (focusBtn ?? launch), { silent: true });
  }

  private selectMech(id: MechId): void {
    const m = MECHS[id];
    if (!save.isUnlocked(m.unlock)) {
      if (m.unlock && buyUnlock(m.unlock)) sfx('buy');
      else {
        sfx('ui_deny');
        this.mechId = id;
        this.build();
        return;
      }
    }
    this.mechId = id;
    save.data.lastMech = id;
    save.persist();
    this.build();
  }

  private pickCosmetic(id: string, unlock?: string): void {
    if (!save.isUnlocked(unlock)) {
      if (!unlock || !buyUnlock(unlock)) {
        sfx('ui_deny');
        return;
      }
      sfx('buy');
    }
    save.data.cosmetics[this.mechId] = id;
    save.persist();
    this.build();
  }

  private toggleChallenge(id: string): void {
    const list = save.data.challenges;
    const i = list.indexOf(id);
    if (i >= 0) list.splice(i, 1);
    else list.push(id);
    save.persist();
    this.build();
  }

  private buy(id: string): void {
    if (buyUnlock(id)) sfx('buy');
    else sfx('ui_deny');
    this.build();
  }

  private launch(): void {
    const run = startRun({ mechId: this.mechId, challenges: [...save.data.challenges] });
    save.data.lastMech = this.mechId;
    save.persist();
    sfx('ui_confirm');
    this.cameras.main.fadeOut(300, 5, 7, 11);
    this.cameras.main.once(Phaser.Cameras.Scene2D.Events.FADE_OUT_COMPLETE, () => this.scene.start(SCENES.GAME, { nodeId: run.nodeId }));
  }

  update(_t: number, delta: number): void {
    this.t += delta / 1000;
    this.nav?.update();
    this.view?.update(delta / 1000);
  }
}
